import { Badge } from "@/components/ui/badge";
import {
  AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { TrendingUp } from "lucide-react";

export function SearchRankingsSection({ searchRankings }) {
  if (!searchRankings) return null;
  return (
    <AccordionItem value="search-rankings" className="border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-slate-50">
        <div className="flex items-center gap-2 flex-1">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Google Search Position</span>
          <Badge className="text-[8px] bg-emerald-100 text-emerald-600 border-emerald-200 h-3.5 ml-1" variant="outline">LIVE</Badge>
          <div className="flex items-center gap-3 ml-auto mr-3 text-[10px]">
            <span className="text-blue-600 font-semibold">Visibility: {searchRankings.visibility_score}/100</span>
            <span className="text-emerald-600">{searchRankings.first_page_count} on page 1</span>
            <span className="text-muted-foreground">{searchRankings.total_tracked} keywords</span>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4">
        <div className="mb-3 p-2.5 rounded-lg bg-blue-50/50 border border-blue-100">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Visibility</p><p className="text-lg font-bold text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{searchRankings.visibility_score}<span className="text-[10px] font-normal text-slate-400">/100</span></p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Top 3</p><p className="text-lg font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{searchRankings.top_3_count}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Page 1</p><p className="text-lg font-bold text-teal-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{searchRankings.first_page_count}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tracked</p><p className="text-lg font-bold text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{searchRankings.total_tracked}</p></div>
          </div>
        </div>
        <div className="space-y-1">
          {searchRankings.rankings?.map((r, i) => (
            <div key={i} className="flex items-center gap-3 p-2 rounded border border-slate-100 hover:bg-slate-50/50">
              <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
                r.position === null ? "bg-slate-100 text-slate-400" :
                r.position <= 3 ? "bg-emerald-100 text-emerald-700" :
                r.position <= 10 ? "bg-teal-100 text-teal-700" :
                r.position <= 30 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"
              }`}>{r.position ?? "--"}</div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium truncate">{r.keyword}</p>
                <p className="text-[9px] text-muted-foreground">{r.search_volume?.toLocaleString()} monthly searches</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.position && <Badge variant="outline" className={`text-[9px] h-4 ${r.position <= 10 ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>Page {r.page}</Badge>}
                {r.trend === "up" && <TrendingUp className="w-3 h-3 text-emerald-500" />}
                {r.trend === "down" && <TrendingUp className="w-3 h-3 text-rose-500 rotate-180" />}
              </div>
            </div>
          ))}
          {(!searchRankings.rankings || searchRankings.rankings.length === 0) && (
            <p className="text-[11px] text-muted-foreground py-2">
              {searchRankings.total_tracked > 0
                ? `Ranking for ${searchRankings.total_tracked} keywords (${searchRankings.first_page_count} on page 1). Per-keyword detail requires the keyword-tracking add-on.`
                : "No organic keyword rankings detected — a strong opening to pitch local SEO."}
            </p>
          )}
        </div>
        <p className="text-[9px] text-muted-foreground italic mt-2">Source: SEOptimer (live data)</p>
      </AccordionContent>
    </AccordionItem>
  );
}
